import React from 'react'
import { ArrowLeftRight, ChevronDown } from 'lucide-react'

import { nutrimaticDevice } from '@/nutrimatic'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { cn } from '@/lib/utils'
import CurrencyLogo from '../../components/CurrencyLogo'
import { Currency } from '../../sdk'
import { useAppWeb3 } from '../../hooks'
import { useCurrencyBalance } from '../../state/wallet/hooks'
import { AmountInput } from './amount-input'
import { Button } from '@/ui/button'

export function TokenAmountRow({
  label,
  value,
  currency,
  onAmountChange,
  onTokenSelect,
  onMax,
  showMaxButton,
  id,
  className,
}: {
  label: string
  value: string
  currency?: Currency | null
  onAmountChange: (value: string) => void
  onTokenSelect: () => void
  onMax?: () => void
  showMaxButton?: boolean
  id?: string
  className?: string
}) {
  const { account } = useAppWeb3()
  const balance = useCurrencyBalance(account ?? undefined, currency ?? undefined)

  return (
    <div
      className={cn(
        'surface-inset rounded-xl p-[var(--panel-padding)] transition-colors focus-within:border-primary/40',
        className
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {balance ? (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            Balance: {balance.toSignificant(4)}
            {showMaxButton && onMax ? (
              <button
                type="button"
                onClick={onMax}
                className="ml-2 text-primary hover:underline"
              >
                MAX
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onTokenSelect}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 text-sm font-semibold hover:bg-muted"
        >
          {currency ? (
            <>
              <CurrencyLogo currency={currency} size="24px" />
              {currency.symbol}
            </>
          ) : (
            'Select'
          )}
          <ChevronDown className="size-4 opacity-60" />
        </button>
        <AmountInput
          id={id}
          value={value}
          onUserInput={onAmountChange}
          className="min-w-0 flex-1 bg-transparent text-right font-mono text-2xl tabular-nums text-foreground outline-none placeholder:text-muted-foreground/50"
        />
      </div>
    </div>
  )
}

export function SwapDirectionToggle({
  onSwitch,
  disabled,
  className,
  variant = 'default',
}: {
  onSwitch: () => void
  disabled?: boolean
  className?: string
  variant?: 'default' | 'nutrimatic'
}) {
  const t = useTranslation()

  if (variant === 'nutrimatic') {
    return (
      <div className={cn('relative z-10 flex justify-center py-1', className)}>
        <button
          type="button"
          className={cn(nutrimaticDevice.physicalButton, 'size-10')}
          onClick={onSwitch}
          disabled={disabled}
          aria-label={t.common.invertSwapDirection}
        >
          <ArrowLeftRight className="size-4" aria-hidden />
        </button>
      </div>
    )
  }

  return (
    <div className={cn('relative z-10 -my-2 flex justify-center', className)}>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-9 rounded-full border-border bg-background shadow-sm"
        onClick={onSwitch}
        disabled={disabled}
        aria-label={t.common.invertSwapDirection}
      >
        ↓
      </Button>
    </div>
  )
}

export function DenseStatRow({
  label,
  value,
  onClick,
  className,
}: {
  label: string
  value: React.ReactNode
  onClick?: () => void
  className?: string
}) {
  const Wrapper = onClick ? 'button' : 'div'
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-2 py-1 text-sm',
        onClick && 'cursor-pointer hover:text-primary',
        className
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </Wrapper>
  )
}
